import React, { useState, useEffect } from 'react';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { getSupportTickets, updateSupportTicket } from '../../services/api/support';
import { format } from 'date-fns';
import { Search, Filter } from 'lucide-react';
import type { SupportTicket } from '../../types';
import { createLogger } from '../../utils/logger';

const logger = createLogger('support-tickets');

const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await getSupportTickets();
      if (!response.success) {
        throw new Error('Failed to fetch tickets');
      }
      setTickets(response.tickets);
      logger.info('Support tickets fetched', { count: response.tickets.length });
    } catch (error) {
      logger.error('Error fetching tickets', error);
      setError('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    try {
      await updateSupportTicket(ticketId, newStatus);
      await fetchTickets();
      logger.info('Ticket status updated', { ticketId, newStatus });
    } catch (error) {
      logger.error('Error updating ticket', error);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user.organization.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Support Tickets</h2>
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      <Table>
        <thead>
          <tr>
            <th>Subject</th>
            <th>Message</th>
            <th>User</th>
            <th>Status</th>
            <th>Priority</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTickets.map((ticket) => (
            <tr key={ticket.id}>
              <td>{ticket.subject}</td>
              <td className="max-w-md">
                <div className="truncate">{ticket.message}</div>
              </td>
              <td>
                <div className="space-y-1">
                  <div className="font-medium">{ticket.user.name}</div>
                  <div className="text-sm text-gray-500">{ticket.user.email}</div>
                  <div className="text-sm text-gray-500">{ticket.user.organization}</div>
                </div>
              </td>
              <td>
                <Badge
                  variant={
                    ticket.status === 'open' ? 'error' :
                    ticket.status === 'in_progress' ? 'warning' :
                    'success'
                  }
                >
                  {ticket.status.replace('_', ' ')}
                </Badge>
              </td>
              <td>
                <Badge
                  variant={
                    ticket.priority === 'urgent' ? 'error' :
                    ticket.priority === 'high' ? 'warning' :
                    'default'
                  }
                >
                  {ticket.priority}
                </Badge>
              </td>
              <td>{format(new Date(ticket.createdAt), 'PPp')}</td>
              <td>
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {filteredTickets.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No tickets found matching your search criteria
        </div>
      )}
    </div>
  );
};

export default SupportTickets;